import { VisuallyHidden, VStack } from '@chakra-ui/react';
import { Button, Icon, IconName } from '@payflip/ui';
import { List, PayflipLogo, TenantLogo, useFeatureFlag } from '@payflip/ui/v2';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useLocation } from 'react-router';
import { environment } from '../../environment';
import { useIsPayflipCompany } from '../../hooks/use-is-payflip-company';
import { LocaleContext } from '../locale-provider';
import { Message } from '../message';
import { getStaticProps } from './get-static-props';
import {
  useGetCartChoicesCountQuery,
  useGetUserTenantQuery
} from './queries.generated';
import {
  Badge,
  BottomArea,
  CollapseButtonWrapper,
  IconWrapper,
  Link,
  Logo,
  Wrapper
} from './styled';

export function Nav(): JSX.Element | null {
  const { data: userData } = useGetUserTenantQuery();
  const { locale } = useContext(LocaleContext);
  const isEsopTrackerEnabled = useFeatureFlag('enableEsopTracker');
  const { isPayflipCompany } = useIsPayflipCompany();

  const { data: staticProps } = useQuery(['navigation', locale], () =>
    getStaticProps(locale)
  );

  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const { data } = useGetCartChoicesCountQuery({
    fetchPolicy: 'cache-and-network'
  });

  useEffect(() => {
    if (
      typeof window?.localStorage?.getItem === 'function' &&
      window.localStorage.getItem('prefer-collapsed-nav') !== null
    ) {
      setCollapsed(
        window.localStorage.getItem('prefer-collapsed-nav') === 'true'
      );
    }
  }, []);

  const toggleAndPersistCollapseMenu = useCallback(() => {
    setCollapsed((collapsed) => {
      if (typeof window?.localStorage?.setItem === 'function') {
        window.localStorage.setItem(
          'prefer-collapsed-nav',
          collapsed ? 'false' : 'true'
        );
      }

      return !collapsed;
    });
  }, []);

  const choicesToCheckoutCount = useMemo(() => {
    if (typeof data !== 'object') {
      return 0;
    }

    return data.choices.length;
  }, [data]);

  const cmsData = useMemo(() => {
    if (typeof staticProps === 'undefined') {
      return null;
    }

    return staticProps.props.data.employeeNavigation.data?.attributes ?? null;
  }, [staticProps]);

  const userTenant = useMemo(() => {
    if (typeof userData === 'undefined') {
      return null;
    }
    return userData.user?.tenant ?? null;
  }, [userData]);

  const isPartner = environment.IS_PARTNER === 'true';

  return (
    <Wrapper collapsed={collapsed}>
      <Logo collapsed={collapsed}>
        {userTenant !== null && (
          <TenantLogo userTenant={userTenant} noText={collapsed} />
        )}
      </Logo>
      <List>
        <VStack alignItems="flex-start">
          <Link to="/" selected={location.pathname === '/'}>
            <IconWrapper selected={location.pathname === '/'}>
              <Icon name={IconName.Home} />
            </IconWrapper>
            {collapsed ? (
              <VisuallyHidden>{cmsData?.overviewLinkText}</VisuallyHidden>
            ) : (
              <Message text={cmsData?.overviewLinkText} fallback="Overview" />
            )}
          </Link>
          <Link
            to="/benefits"
            selected={location.pathname.startsWith('/benefits')}
          >
            <IconWrapper selected={location.pathname.startsWith('/benefits')}>
              <Icon name={IconName.LocalOffer} />
            </IconWrapper>
            {collapsed ? (
              <VisuallyHidden>{cmsData?.benefitsLinkText}</VisuallyHidden>
            ) : (
              <Message text={cmsData?.benefitsLinkText} fallback="Benefits" />
            )}
          </Link>
          <Link
            to="/your-space"
            selected={location.pathname.startsWith('/your-space')}
          >
            <IconWrapper selected={location.pathname.startsWith('/your-space')}>
              <Icon name={IconName.Grid} />
            </IconWrapper>
            {collapsed ? (
              <VisuallyHidden>{cmsData?.yourSpaceLinkText}</VisuallyHidden>
            ) : (
              <Message
                text={cmsData?.yourSpaceLinkText}
                fallback="Your space"
              />
            )}
          </Link>
          <Link to="/cart" selected={location.pathname.startsWith('/cart')}>
            <IconWrapper selected={location.pathname.startsWith('/cart')}>
              <Icon name={IconName.Basket} />
              {collapsed === true && choicesToCheckoutCount > 0 && (
                <Badge collapsed={collapsed} />
              )}
            </IconWrapper>
            {collapsed ? (
              <VisuallyHidden>{cmsData?.cartLinkText}</VisuallyHidden>
            ) : (
              <span>
                <Message text={cmsData?.cartLinkText} fallback="Cart" />
                {collapsed === false && choicesToCheckoutCount > 0 && <Badge />}
              </span>
            )}
          </Link>

          {isEsopTrackerEnabled && isPayflipCompany ? (
            <Link
              to="/stocks"
              selected={location.pathname.startsWith('/stocks')}
            >
              <IconWrapper selected={location.pathname.startsWith('/stocks')}>
                <Icon name={IconName.Euro} />
              </IconWrapper>
              {collapsed ? (
                <VisuallyHidden>Stocks</VisuallyHidden>
              ) : (
                <Message text="Stocks" fallback="Stocks" />
              )}
            </Link>
          ) : null}
        </VStack>
      </List>
      <BottomArea>
        {isPartner && (
          <Logo collapsed={collapsed}>
            <PayflipLogo noText={collapsed} reseller />
          </Logo>
        )}
        <CollapseButtonWrapper collapsed={collapsed}>
          <Button
            icon={
              collapsed ? IconName.DoubleArrowRight : IconName.DoubleArrowLeft
            }
            variant="tertiary"
            size="small"
            onClick={toggleAndPersistCollapseMenu}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? '' : 'Collapse'}
          </Button>
        </CollapseButtonWrapper>
      </BottomArea>
    </Wrapper>
  );
}
